import React, { MutableRefObject, useCallback, useEffect,  useState } from 'react';
import { AgGridReact } from 'ag-grid-react';

import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, showSuccess, showWarning } from 'src/utils/toast/toastUtil';
import { downloadSettingExcel } from 'src/utils/blob/blobUtil';

import {
    downloadExcel_TQC1020_R01,
    getTQC1020_R01Detail,
    getTQC1020_R01Header,
    saveTQC1020_R01,
} from './Api_TQC1020_R01';
import {  defaultTQC1020_R01ResultDto, TQC1020_R01HeaderDto, TQC1020_R01DetailDto } from './Dto_TQC1020_R01';
import { useTQC1020_R01Store } from 'src/containers/mes/TQC/TQC1020_R01/Store_TQC1020_R01';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { InsertRowHandler } from 'src/utils/grid/GridHandlerContent';
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	TQC1020_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';

interface Props {
    gridRef: MutableRefObject<AgGridReact<TQC1020_R01HeaderDto> | null>;
    detailGridRef: MutableRefObject<AgGridReact<TQC1020_R01DetailDto> | null>;
}

export const Content_TQC1020_R01 = ({ gridRef, detailGridRef }: Props) => {
    const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { faultyValueList } = useCommonCodeStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();

    const {
        TQC1020_R01Filter,
        TQC1020_R01List,
        setTQC1020_R01List,
        selectedTQC1020_R01,
        setSelectedTQC1020_R01,
        TQC1020_R01DetailList,
        setTQC1020_R01DetailList,
        insertTQC1020_R01DetailList,
        setOriginalTQC1020_R01DetailList,
        deletedTQC1020_R01DetailList,
        removeDeletedTQC1020_R01DetailList,
        detailFetchTrigger,
        setDetailFetchTrigger,
        setScrollTop,
        setDetailScrollTop,
        initData: initStoreData,
    } = useTQC1020_R01Store();

    const [clickable, setClickable] = useState(true);

    const formatDate = (date?: Date) => {
        if (!date) return '';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

	const defaultTQC1020_R01DetailRow = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(TQC1020_R01_ROW_VALIDATE, {
				faultyValueList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultTQC1020_R01ResultDto };
		}

		return {
			...defaultTQC1020_R01ResultDto,
			badCase: faultyValueList[0].minorCd,
			inserted: true,
		};
	}, [faultyValueList, validateCommonCodeLists]);

    const handleInsertClick = useCallback((e: React.MouseEvent, rowIndex?: number) => {
        const isGridInsert = rowIndex !== undefined;

        InsertRowHandler({
            event: e,
            dataList: TQC1020_R01DetailList,
            rowData: isGridInsert ? TQC1020_R01DetailList[rowIndex] : { ...defaultTQC1020_R01DetailRow(), valueIndex: -1 },
            insertRow: insertTQC1020_R01DetailList,
            defaultRowData: defaultTQC1020_R01DetailRow(),
            ...(isGridInsert && { setClickable }),
        });
    }, [TQC1020_R01DetailList, insertTQC1020_R01DetailList, defaultTQC1020_R01DetailRow, setClickable]);

    // ── 상단 조회 ────────────────────────────────────────────────────────
    const fetchData = useCallback(async () => {
        try {
            setScrollTop(null);
            setDetailScrollTop(null);
            setLoadingSpinner(true);
            const response = await getTQC1020_R01Header({
                inoutClass: TQC1020_R01Filter.inoutClass || undefined,
                compCd: TQC1020_R01Filter.compCd || undefined,
                customCd: TQC1020_R01Filter.customCd || undefined,
                itemCd: TQC1020_R01Filter.itemCd || undefined,
                startDate: formatDate(TQC1020_R01Filter.transportDt?.from),
                endDate: formatDate(TQC1020_R01Filter.transportDt?.to),
                includeCompleted: TQC1020_R01Filter.includeCompleted,
            });

            const data = (response.data.data ?? []).map((item, idx) => ({
                ...item,
                valueIndex: item.valueIndex || idx + 1,
                isModified: false,
            }));

            setTQC1020_R01List(data);
            setTQC1020_R01DetailList([]);
            setOriginalTQC1020_R01DetailList([]);
            setSelectedTQC1020_R01(data.length > 0 ? data[0] : undefined);

            data.length > 0
                ? showSuccess(`조회 완료 (${data.length}건)`)
                : showWarning('조회된 데이터가 없습니다.');
        } finally {
            setLoadingSpinner(false);
        }
    }, [TQC1020_R01Filter, setLoadingSpinner, setTQC1020_R01List, setTQC1020_R01DetailList, setOriginalTQC1020_R01DetailList, setSelectedTQC1020_R01, setScrollTop, setDetailScrollTop]);

    const handleExcelClick = useCallback(async () => {
        try {
            setLoadingSpinner(true);
            const response = await downloadExcel_TQC1020_R01({
                inoutClass: TQC1020_R01Filter.inoutClass || undefined,
                compCd: TQC1020_R01Filter.compCd || undefined,
                customCd: TQC1020_R01Filter.customCd || undefined,
                itemCd: TQC1020_R01Filter.itemCd || undefined,
                startDate: formatDate(TQC1020_R01Filter.transportDt?.from),
                endDate: formatDate(TQC1020_R01Filter.transportDt?.to),
                includeCompleted: TQC1020_R01Filter.includeCompleted,
            });
            if (!response.data.data) {
                showWarning('엑셀로 내보낼 수입검사 데이터가 없습니다.');
                return;
            }
            downloadSettingExcel({
                responseData: response.data.data,
                fileName: '수입검사등록.xlsx',
            });
        } catch (error) {
            console.error('수입검사등록 엑셀 다운로드 실패:', error);
            showWarning('수입검사등록 엑셀 다운로드 중 오류가 발생했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
    }, [TQC1020_R01Filter, setLoadingSpinner]);

    // ── 하단 조회 (상단 행 선택 트리거) ──────────────────────────────────
    const fetchResultList = useCallback(async () => {
        if (!detailFetchTrigger || !selectedTQC1020_R01) return;
        try {
            setLoadingSpinner(true);
            const response = await getTQC1020_R01Detail(
                selectedTQC1020_R01.basOrderNo,
                selectedTQC1020_R01.basOrderItemNo
            );
            const data = (response.data.data ?? []).map((item, idx) => ({
                ...item,
                valueIndex: item.valueIndex || idx + 1,
                orderIndex: item.orderIndex || idx + 1,
                orderIndexSeq: item.orderIndexSeq || 0,
                inserted: false,
                isModified: false,
            }));
            if (data.length > 0) {
                setTQC1020_R01DetailList(data);
                setOriginalTQC1020_R01DetailList(data);
            } else {
                setTQC1020_R01DetailList([]);
                setOriginalTQC1020_R01DetailList([]);
                insertTQC1020_R01DetailList([{ ...defaultTQC1020_R01ResultDto, inserted: true }]);
                showWarning('데이터가 없습니다.');
            }
        } finally {
            setDetailFetchTrigger(false);
            setLoadingSpinner(false);
        }
    }, [
        detailFetchTrigger,
        selectedTQC1020_R01,
        setDetailFetchTrigger,
        setLoadingSpinner,
        setTQC1020_R01DetailList,
        setOriginalTQC1020_R01DetailList,
        insertTQC1020_R01DetailList,
    ]);

    useEffect(() => {
        fetchResultList();
    }, [fetchResultList]);

    // ── 안전망: 선택된 행이 있는데 디테일이 비어있으면 신규행 삽입 ────────
    useEffect(() => {
        if (selectedTQC1020_R01 && TQC1020_R01DetailList.length === 0) {
            insertTQC1020_R01DetailList([{ ...defaultTQC1020_R01ResultDto, inserted: true }]);
        }
    }, [selectedTQC1020_R01, TQC1020_R01DetailList, insertTQC1020_R01DetailList]);

    // ── 하단 저장 ────────────────────────────────────────────────────────
    const saveResultList = useCallback(async () => {
        try {
            setLoadingSpinner(true);

            const modifiedHeaderRows: TQC1020_R01HeaderDto[] = TQC1020_R01List.filter(
                (row: TQC1020_R01HeaderDto) => row.isModified
            );

            const deletedSet = new Set(deletedTQC1020_R01DetailList);

            // 신규 행 (inserted=true, 삭제 대상 아님)
            const insertedRows: TQC1020_R01DetailDto[] = TQC1020_R01DetailList.filter(
                (r: TQC1020_R01DetailDto) => r.inserted && !deletedSet.has(String(r.valueIndex))
            );
            // 수정 행 (isModified=true, inserted 아님, 삭제 대상 아님)
            const modifiedRows: TQC1020_R01DetailDto[] = TQC1020_R01DetailList.filter(
                (r: TQC1020_R01DetailDto) =>
                    r.isModified && !r.inserted && !deletedSet.has(String(r.valueIndex))
            );
            // 삭제 대상 (기존 행만, inspectNo 있는 것)
            const deletedInspectNos: string[] = TQC1020_R01DetailList.filter(
                (r: TQC1020_R01DetailDto) =>
                    deletedSet.has(String(r.valueIndex)) && !r.inserted && !!r.inspectNo
            ).map((r: TQC1020_R01DetailDto) => r.inspectNo);

            const hasDetailChanges =
                insertedRows.length > 0 || modifiedRows.length > 0 || deletedInspectNos.length > 0;

            if (hasDetailChanges && !selectedTQC1020_R01) {
                showWarning('상단 목록에서 입고 건을 선택하세요.');
                return;
            }

            if (
                modifiedHeaderRows.length === 0 &&
                insertedRows.length === 0 &&
                modifiedRows.length === 0 &&
                deletedInspectNos.length === 0
            ) {
                showWarning('변경된 데이터가 없습니다.');
                return;
            }

            const activeDetailRows = hasDetailChanges && selectedTQC1020_R01
                ? TQC1020_R01DetailList
                    .filter((row) => !deletedSet.has(String(row.valueIndex)))
                    .map((row) => ({
                        ...row,
                        basOrderNo: selectedTQC1020_R01.basOrderNo,
                        basOrderItemno: selectedTQC1020_R01.basOrderItemNo,
                        compCd: row.compCd || selectedTQC1020_R01.compCd,
                    }))
                : null;

            const response = await saveTQC1020_R01({
                headerDtoList: modifiedHeaderRows,
                detailDtoList: activeDetailRows,
                basOrderNo: selectedTQC1020_R01?.basOrderNo,
                basOrderItemNo: selectedTQC1020_R01?.basOrderItemNo,
                compCd: selectedTQC1020_R01?.compCd,
            });
            const savedData = response.data.data;
            const updatedHeaderRows = savedData?.headerDtoList ?? [];
            const normalizedTopList = TQC1020_R01List.map((row) => {
                const updated = updatedHeaderRows.find(
                    (item) => item.transportNo === row.transportNo && item.itemNo === row.itemNo
                );
                return updated ? { ...row, ...updated, isModified: false } : row;
            });
            setTQC1020_R01List(normalizedTopList);

            if (hasDetailChanges) {
                const refreshed = (savedData?.detailDtoList ?? []).map((item, idx) => ({
                    ...item,
                    valueIndex: item.valueIndex || idx + 1,
                    orderIndex: item.orderIndex || idx + 1,
                    orderIndexSeq: item.orderIndexSeq || 0,
                    inserted: false,
                    isModified: false,
                }));
                if (refreshed.length > 0) {
                    setTQC1020_R01DetailList(refreshed);
                    setOriginalTQC1020_R01DetailList(refreshed);
                } else {
                    setTQC1020_R01DetailList([]);
                    setOriginalTQC1020_R01DetailList([]);
                    insertTQC1020_R01DetailList([{ ...defaultTQC1020_R01ResultDto, inserted: true }]);
                }

                removeDeletedTQC1020_R01DetailList(deletedTQC1020_R01DetailList);
            }

            showSuccess('저장이 완료되었습니다.');
        } catch {
            showError('저장 중 오류가 발생했습니다.');
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        selectedTQC1020_R01,
        TQC1020_R01List,
        TQC1020_R01DetailList,
        deletedTQC1020_R01DetailList,
        setLoadingSpinner,
        setTQC1020_R01List,
        setTQC1020_R01DetailList,
        setOriginalTQC1020_R01DetailList,
        insertTQC1020_R01DetailList,
        removeDeletedTQC1020_R01DetailList,
    ]);

    // ── 초기화 ───────────────────────────────────────────────────────────
    const initData = useCallback(() => {
        initStoreData();
        gridRef.current?.api?.deselectAll();
        detailGridRef.current?.api?.deselectAll();
        gridRef.current?.api?.setGridOption('rowData', []);
        detailGridRef.current?.api?.setGridOption('rowData', []);
    }, [detailGridRef, gridRef, initStoreData]);

    return { 
		clickable, setClickable,
		defaultTQC1020_R01DetailRow,
		handleInsertClick,
		fetchData, 
		handleExcelClick,
		saveResultList, 
		initData,
	};
};
